import { FormattedMessage, useIntl } from 'react-intl';

import InformationCircleIcon from '@/assets/icons/information-circle.svg';
import { Progress } from '@/components/ui/progress';
import { Tooltip } from '@/components/ui/tooltip';
import { Typography } from '@/components/ui/typography';
import { useEditTargetsContext } from '@/contexts/edit-targets-context';
import { useCurrencySymbol } from '@/hooks/use-currency-symbol';

interface BudgetUsageCardProps {
  targetBudget: number | undefined;
}

export function BudgetUsageCard({ targetBudget }: BudgetUsageCardProps) {
  const intl = useIntl();
  const { formatCurrency } = useCurrencySymbol();
  const { spendStats } = useEditTargetsContext();

  if (!spendStats || !targetBudget) return null;

  const { total, committed } = spendStats;
  const totalSpend = total ?? 0;
  const committedSpend = committed ?? 0;
  const actualSpend = totalSpend - committedSpend;

  return (
    <>
      {/* Divider */}
      <div className="bg-fx-neutral-200 h-px w-full" />

      <div className="border-fx-brand-100 bg-fx-neutral-50 flex flex-col gap-4 rounded-lg border p-4">
        <div className="flex flex-col items-start justify-center gap-2">
          {/* Title */}
          <Typography variant="caption">
            <FormattedMessage
              defaultMessage="How budget is used"
              description="Title for 'How budget is used'"
            />
          </Typography>

          {/* Actual spend */}
          <div className="flex w-full items-center justify-between gap-4">
            <div className="flex items-center gap-1">
              <Typography variant="small1">
                <FormattedMessage
                  defaultMessage="Actual spend:"
                  description="Actual spend label"
                />
              </Typography>
              <Tooltip
                content={intl.formatMessage({
                  defaultMessage:
                    'Amount already spent on campaigns that have completed or are currently running.',
                  description: 'Tooltip content for actual spend',
                })}
              >
                <img src={InformationCircleIcon} alt="Information" />
              </Tooltip>
            </div>
            <Typography variant="small1">
              {formatCurrency(actualSpend)}
            </Typography>
          </div>

          {/* Committed spend */}
          <div className="flex w-full items-center justify-between gap-4">
            <div className="flex items-center gap-1">
              <Typography variant="small1">
                <FormattedMessage
                  defaultMessage="Committed spend:"
                  description="Committed spend label"
                />
              </Typography>
              <Tooltip
                content={intl.formatMessage({
                  defaultMessage:
                    'Budget locked in for non-pausable channels (e.g., ZipRecruiter). This amount will be spent even if campaigns are paused by automation.',
                  description: 'Tooltip content for committed spend',
                })}
              >
                <img src={InformationCircleIcon} alt="Information" />
              </Tooltip>
            </div>
            <Typography variant="small1">
              {formatCurrency(committedSpend)}
            </Typography>
          </div>

          {/* Total spend */}
          <div className="flex w-full items-center justify-between gap-4">
            <Typography variant="small2">
              <FormattedMessage
                defaultMessage="Total spend:"
                description="Total spend label"
              />
            </Typography>
            <Typography variant="small2">
              {formatCurrency(totalSpend)}
            </Typography>
          </div>
        </div>

        <div className="bg-fx-neutral-200 h-px w-full" />

        <div className="flex flex-col items-start justify-center gap-2">
          {/* Title */}
          <Typography variant="caption">
            <FormattedMessage
              defaultMessage="How budget is used"
              description="Title for 'How budget is used'"
            />
          </Typography>

          <Progress
            value={Math.min(
              Math.max((totalSpend / targetBudget) * 100, 0),
              100,
            )}
            rootClassName="h-2 rounded-none"
            className="bg-fx-primary"
          />

          <div className="flex w-full justify-between gap-4">
            <Typography variant="caption">
              <FormattedMessage
                defaultMessage="Total spent: {totalSpend}"
                values={{ totalSpend: formatCurrency(totalSpend) }}
                description="Total spent label"
              />
            </Typography>
            <Typography variant="caption">
              <FormattedMessage
                defaultMessage="Remaining: {remaining}"
                values={{
                  remaining: formatCurrency(targetBudget - totalSpend),
                }}
                description="Remaining label"
              />
            </Typography>
          </div>
        </div>
      </div>
    </>
  );
}
